import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { API, getIcon } from "@/lib/constants";
import ServiceDemo from "@/components/ServiceDemos";

const SEO = {
  title: "Digital Marketing Services Dallas | SEO, PPC, Web Design & Social Media | DFW Agency",
  description: "Full-service digital marketing agency in Dallas-Fort Worth. Local SEO, Google Ads management, WordPress web design, PPC advertising, social media marketing, content marketing & lead generation. Free consultation for DFW businesses!",
  keywords: "Dallas SEO services, DFW digital marketing agency, Local SEO Dallas, Google Business Profile optimization, WordPress web design Dallas, PPC management Dallas, social media marketing Dallas, content marketing DFW, Google Ads Dallas, lead generation Dallas, website optimization Dallas, marketing automation Dallas",
  image: "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG",
  url: "https://digitalmedianinja.com/services"
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    // Ensure title is set
    document.title = SEO.title;
    
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="services-page">
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <meta name="keywords" content={SEO.keywords} />
        <link rel="canonical" href={SEO.url} />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:image" content={SEO.image} />
        <meta property="og:url" content={SEO.url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
      </Helmet>
      
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#00E676] uppercase tracking-wider text-sm font-medium">Our Services</span>
          <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Complete Digital Marketing Solutions
          </h1>
          <p className="text-gray-400 text-lg">
            From traditional SEO to cutting-edge AEO and GEO optimization, we have the expertise to grow your Dallas business.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service) => (
            <Card 
              key={service.id}
              className="bg-[#0A0A0A] border-white/5 hover:border-[#00E676]/30 transition-all group overflow-hidden"
              data-testid={`service-page-${service.id}`}
            >
              <div className="border-b border-white/[0.06]">
                <ServiceDemo slug={service.slug} aspect="aspect-[21/9]" />
              </div>
              <div className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-[#00E676] group-hover:scale-110 transition-transform">
                  {getIcon(service.icon, "w-10 h-10")}
                </span>
                <h3 className="text-2xl font-bold text-white group-hover:text-[#00E676] transition-colors">
                  {service.name || service.title}
                </h3>
              </div>
              <p className="text-gray-400 mb-6">{service.description}</p>
              
              <div className="mb-6">
                <h4 className="font-medium text-white mb-3">Key Features:</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(service.features || []).slice(0, 6).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                      <CheckCircle2 className="w-4 h-4 text-[#00E676] shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <Link to={`/services/${service.slug}`}>
                <Button className="btn-outline w-full group-hover:border-[#00E676] group-hover:text-[#00E676]">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
